import React from 'react';
import { Text, StyleSheet } from '@react-pdf/renderer';
import { JournalStatus } from '../../../types/finance';
import { formatPaisa } from '../../../utils/paisa';
import { pdfColors, pdfSpacing, fmtDate, type DataTableColumn } from '../../../utils/pdf';

/** Cells shared by the finance movement tables — ledger, cash & bank, reconciliation — so they read alike. */

const styles = StyleSheet.create({
  muted: { color: pdfColors.textMuted },
  faint: { color: pdfColors.textFaint },
  voucher: { fontWeight: 600, color: pdfColors.textPrimary },
  amount: { fontWeight: 600 },
  in: { fontWeight: 600, color: pdfColors.success },
  out: { fontWeight: 600, color: pdfColors.danger },
  note: { color: pdfColors.textMuted, fontStyle: 'italic' },
  sectionTitle: { marginTop: pdfSpacing.lg, marginBottom: 6, fontSize: 8.5, fontWeight: 700, color: pdfColors.textPrimary, textTransform: 'uppercase', letterSpacing: 0.6 },
});

/** A figure column's width: never under `min`, and wide enough that its longest figure (7.5pt, semi-bold) stays on one line. */
export const figureWidth = (figures: string[], min = 84): number =>
  Math.max(min, Math.ceil(Math.max(0, ...figures.map(f => f.length)) * 4.4 + 14));

/** A figure on one side of a line; a quiet dash when the line is on the other side. */
export const Amount: React.FC<{ paisa: number; currency: string; tone?: 'in' | 'out' }> = ({ paisa, currency, tone }) =>
  (paisa ? <Text style={tone ? styles[tone] : styles.amount}>{formatPaisa(paisa, currency)}</Text> : <Text style={styles.faint}>—</Text>);

/** What the line was, and whether it was reversed or is the reversal. The mark is one string: split text runs
 *  read to the layout engine as syllables of one word, which it may hyphenate apart. */
export const Description: React.FC<{ text: string; status: JournalStatus; isReversal: boolean }> = ({ text, status, isReversal }) => {
  const mark = isReversal ? 'reversal' : status === 'REVERSED' ? 'reversed' : null;
  return <Text>{text}{mark && <Text style={styles.note}>{` (${mark})`}</Text>}</Text>;
};

/** The leading columns every movement table opens with: the line number, the date and the voucher. */
export function leadColumns<Row extends { entryDate: string; entryNo: string }>(): Array<DataTableColumn<Row>> {
  return [
    { key: 'n', header: '#', width: 22, align: 'center', render: (_r, i) => <Text style={styles.faint}>{i + 1}</Text> },
    { key: 'date', header: 'Date', width: 58, render: r => fmtDate(r.entryDate) },
    { key: 'voucher', header: 'Voucher', width: 80, render: r => <Text style={styles.voucher}>{r.entryNo}</Text> },
  ];
}

/** A titled table inside a report (Outstanding items, Cleared on this statement). */
export const TableTitle: React.FC<{ children: string }> = ({ children }) => (
  <Text style={styles.sectionTitle} minPresenceAhead={60}>{children}</Text>
);
